import { useMemo } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  ReferenceLine,
} from "recharts";
import { dailyPnL } from "@/lib/aggregate";
import type { ScalpPosition } from "@/store/api";
import { fmtUsd } from "@/lib/format";

interface Props {
  positions: ScalpPosition[];
  days?: number;
  height?: number;
}

export function DailyPnL({ positions, days = 14, height = 200 }: Props) {
  const data = useMemo(() => dailyPnL(positions, days), [positions, days]);
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data} margin={{ top: 5, right: 8, left: 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
        <XAxis
          dataKey="date"
          stroke="hsl(var(--muted))"
          fontSize={11}
          tickFormatter={(d: string) => d.slice(5)}
          minTickGap={20}
        />
        <YAxis
          stroke="hsl(var(--muted))"
          fontSize={11}
          tickFormatter={(v: number) => `$${v}`}
          width={45}
        />
        <Tooltip
          contentStyle={{
            background: "hsl(var(--bg-2))",
            border: "1px solid hsl(var(--border))",
            borderRadius: 6,
            fontSize: 12,
          }}
          formatter={(v) => fmtUsd(typeof v === "number" ? v : Number(v))}
        />
        <ReferenceLine y={0} stroke="hsl(var(--muted))" strokeDasharray="3 3" />
        <Bar dataKey="pnl">
          {data.map((d) => (
            <Cell
              key={d.date}
              fill={d.pnl > 0 ? "hsl(var(--good))" : d.pnl < 0 ? "hsl(var(--bad))" : "hsl(var(--muted))"}
              opacity={d.count === 0 ? 0.3 : 1}
            />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}
